import { T, tnum, pct2 } from '../../../../theme/tokens';
import { Ask } from './Panel';

/* A sparkline, and ONLY where a real series exists.
   🔴 The reference mockup draws one on all eight tiles. Five of ours now have a
   genuine six-month series behind them — revenue, therapist cost, gross, meetings
   and revenue-per-patient all come out of `trend`. The remaining three do not, and
   they get no sparkline rather than a drawn-from-nothing squiggle: a decorative
   chart on a financial tile is a lie that looks like evidence. */
function Spark({ points, color }: { points: number[]; color: string }) {
  if (points.length < 2) return null;
  const W = 74, H = 26, P = 3;
  const lo = Math.min(...points), hi = Math.max(...points);
  const span = hi - lo || 1;
  const x = (i: number) => P + (i / (points.length - 1)) * (W - P * 2);
  const y = (v: number) => H - P - ((v - lo) / span) * (H - P * 2);
  const d = points.map((v, i) => `${i ? 'L' : 'M'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden style={{ flexShrink: 0, display: 'block' }}>
      {/* One faint rule per month, so the eye can count where a dip sits — "תסמן
          לי חודשים בקו ישר כזה בקטנה" (Ohad, 02/11). Rules, not labels: at 74px
          wide there is no room for six month names. */}
      {points.map((_, i) => (
        <line key={i} x1={x(i)} x2={x(i)} y1={1} y2={H - 1} stroke={T.border} strokeWidth={1} />
      ))}
      <path d={`${d} L ${x(points.length - 1).toFixed(1)} ${H} L ${x(0)} ${H} Z`} fill={color} opacity={0.1} />
      <path d={d} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(points.length - 1)} cy={y(points[points.length - 1])} r={2.2} fill={color} />
    </svg>
  );
}

interface Props {
  icon: string;
  /* The icon carries the tile's colour. In the reference every glyph is a solid
     brand hue rather than a grey chip — it is what makes the row read as a set of
     instruments instead of a row of boxes. */
  iconColor?: string;
  label: string;
  value: string;
  /* There is no line under the value. One lived here until 02/11 — the base of the
     comparison, "מ-₪54,285 ב-01/10–02/10" — and Ohad took it out: "זה לא מעניין".
     The base still exists, in the "?" beside the label; and a two-line sub was what
     pushed one tile's number above its neighbours'. */
  delta?: number;
  /* Set on metrics where UP IS BAD — therapist cost, no-show loss. Without it a
     rising cost would be painted green, which is how a dashboard teaches people to
     stop reading it. */
  invert?: boolean;
  spark?: number[];
  ask?: string;
}

export function Tile({ icon, iconColor, label, value, delta, invert, spark, ask }: Props) {
  const up = delta !== undefined && delta >= 0;
  const good = delta === undefined ? false : (invert ? !up : up);
  const deltaColor = delta === undefined ? T.inkFaint : good ? T.green : T.red;

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: T.radius,
      boxShadow: T.shadow,
      padding: '14px 16px 13px',
      display: 'flex', flexDirection: 'column',
      // Every tile is the height of the tallest: a two-line label used to make one
      // tile deeper than its neighbours and the whole row sat crooked. Reserving the
      // label's two lines and pinning the value block below keeps the numbers on one
      // straight line across the row.
      height: '100%', minHeight: 124,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, minHeight: 32 }}>
        <span aria-hidden style={{
          fontSize: 15, lineHeight: '20px', color: iconColor ?? T.blue, flexShrink: 0,
        }}>
          {icon}
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
          {label}
        </span>
        {ask && <Ask>{ask}</Ask>}
      </div>

      <div style={{ marginTop: 'auto' }}>
        <div style={{
          ...tnum, fontSize: 26, fontWeight: 800, color: T.ink,
          letterSpacing: '-.03em', lineHeight: 1.1,
        }}>
          {value}
        </div>

        {/* Sparkline on the leading edge, change badge on the far one — the shape
            first, then the verdict, as in the reference. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, minHeight: 26 }}>
          {spark && <Spark points={spark} color={deltaColor === T.inkFaint ? T.blue : deltaColor} />}
          <span style={{ marginInlineStart: 'auto' }}>
            {/* No arrow rather than a zero: where the payload carries no monthly
                series, "0%" would invent a finding. */}
            {delta === undefined ? (
              <span
                title="אין סדרה חודשית למדד הזה — אי אפשר להשוות לחודש קודם בלי להמציא מספר"
                style={{ fontSize: 11, color: T.inkFaint, cursor: 'help' }}
              >
                ללא השוואה
              </span>
            ) : (
              <span style={{
                ...tnum, display: 'inline-flex', alignItems: 'center', gap: 2,
                fontSize: 12, fontWeight: 700, color: deltaColor,
                background: good ? T.greenTint : T.redTint,
                borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap',
              }}>
                {/* 🔴 No "+" sign. "+115%" inside an RTL line is reordered to
                    "115%+", which reads as "115% or more". The arrow carries the
                    direction already, so the sign only added a character bidi
                    could move (found 19/10). */}
                <span aria-hidden>{up ? '↑' : '↓'}</span>
                {pct2(Math.abs(delta))}
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
